/* P.Louise — Skincare routine bundle builder V2
 * One mandatory hero product that is locked into every bundle, plus a
 * "choose four or more" selection of product VARIANTS. Supports tiered pack
 * pricing or a straight percentage discount, a one-time / subscribe
 * (Recharge selling plan) toggle, and a Shopify cart/add.js checkout.
 */
(function () {
  const SVG_X = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';
  const SVG_LOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 10V7a5 5 0 0 1 10 0v3"/><rect x="4.5" y="10" width="15" height="10.5" rx="3"/></svg>';

  function formatMoney(cents, format) {
    const value = (cents / 100).toFixed(2);
    const [whole, decimal] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = (format || '£{{amount}}').match(placeholder);
    const key = match ? match[1] : 'amount';
    const amount = key === 'amount_no_decimals' ? withCommas
      : key === 'amount_with_comma_separator' ? `${withCommas},${decimal}`
      : key === 'amount_no_decimals_with_comma_separator' ? withCommas
      : `${withCommas}.${decimal}`;
    return (format || '£{{amount}}').replace(placeholder, amount);
  }

  class SkincareBundleBuilderV2 {
    constructor(root) {
      this.root = root;
      this.money = (c) => formatMoney(c, root.dataset.moneyFormat);

      // Tiers: parallel size/price arrays → sorted [{size, price(pence)}].
      // Sizes count the CHOSEN items only — the mandatory item is separate.
      // Prices arrive in pounds from the section settings, so convert to
      // pence to match Shopify's money (variant.price) and formatMoney.
      const sizes = (root.dataset.tierSizes || '').split(',').filter(Boolean).map(Number);
      const prices = (root.dataset.tierPrices || '').split(',').filter(Boolean).map(Number);
      this.tiers = sizes
        .map((size, i) => ({ size, price: Math.round((prices[i] || 0) * 100) }))
        .filter((t) => t.size > 0)
        .sort((a, b) => a.size - b.size);

      this.pricingMode = root.dataset.pricingMode === 'percent' || !this.tiers.length ? 'percent' : 'tiers';
      this.percentDiscount = parseFloat(root.dataset.percentDiscount) || 0;

      // "Four or more": minimum is required, maximum is optional (0 = none).
      this.minChoices = Math.max(1, parseInt(root.dataset.minChoices, 10) || 1);
      const maxAttr = parseInt(root.dataset.maxChoices, 10) || 0;
      this.maxChoices = maxAttr > 0 ? Math.max(maxAttr, this.minChoices) : Infinity;

      this.mandatoryIncluded = root.dataset.mandatoryIncluded !== 'false';
      // Quantity stepper off => each card is a single on/off choice.
      this.quantityEnabled = root.dataset.quantity !== 'false';
      this.subscriptionEnabled = root.dataset.subscription === 'true';
      this.subscribeDiscount = parseFloat(root.dataset.subscribeDiscount) || 0;
      this.purchaseType = 'onetime';

      // Elements
      this.itemNoun = root.dataset.itemNoun || 'options';

      this.requiredCard = root.querySelector('[data-sbb-required-card]');
      this.chipBar = root.querySelector('[data-sbb-chipbar]');
      this.chipsRow = root.querySelector('[data-sbb-chips]');
      this.chipPrev = root.querySelector('[data-sbb-chip-prev]');
      this.chipNext = root.querySelector('[data-sbb-chip-next]');
      this.grid = root.querySelector('[data-sbb-grid]');
      this.cards = Array.from(root.querySelectorAll('[data-sbb-card]'));
      this.empty = root.querySelector('[data-sbb-empty]');
      this.countline = root.querySelector('[data-sbb-countline]');
      this.clearBtn = root.querySelector('[data-sbb-clear]');

      this.tierEls = Array.from(root.querySelectorAll('[data-sbb-tier]'));
      this.progressLabel = root.querySelector('[data-sbb-progress-label]');

      this.purchaseOpts = Array.from(root.querySelectorAll('[data-sbb-purchase-opt]'));

      this.picksEl = root.querySelector('[data-sbb-picks]');
      this.fullPriceEl = root.querySelector('[data-sbb-fullprice]');
      this.priceEl = root.querySelector('[data-sbb-price]');
      this.saveEl = root.querySelector('[data-sbb-save]');
      this.cta = root.querySelector('[data-sbb-cta]');
      this.panel = root.querySelector('[data-sbb-panel]');
      this.peekToggle = root.querySelector('[data-sbb-peek-toggle]');
      this.peekCta = root.querySelector('[data-sbb-peek-cta]');

      this.layout = root.querySelector('.sbb-layout');
      this.side = root.querySelector('.sbb-side');

      this.toast = root.querySelector('[data-sbb-toast]');
      this.toastInner = root.querySelector('[data-sbb-toast-inner]');
      this.accordionTriggers = Array.from(root.querySelectorAll('[data-sbb-accordion-trigger]'));

      this.category = 'All';
      this.picks = []; // chosen variant id strings, duplicates allowed

      this.buildChips();
      this.bind();
      this.filter();
      this.render();
      this.updateStickyOffset();
      this.updateChipArrows();
      this.updateSticky();
      window.addEventListener('resize', () => { this.updateStickyOffset(); this.updateChipArrows(); this.updateSticky(); });
      window.addEventListener('scroll', () => {
        if (this._stickyRaf) return;
        this._stickyRaf = requestAnimationFrame(() => { this._stickyRaf = null; this.updateSticky(); });
      }, { passive: true });
    }

    // Desktop: keep the right-hand summary column pinned as the collection
    // scrolls, using a transform so it works even when an ancestor's overflow
    // or transform would disable CSS position: sticky.
    updateSticky() {
      const side = this.side;
      const layout = this.layout;
      if (!side || !layout) return;
      if (!window.matchMedia('(min-width: 960px)').matches) {
        side.style.transform = '';
        return;
      }
      const pad = 20; // .sbb-layout padding
      const top = 20; // gap from the viewport top
      const rect = layout.getBoundingClientRect();
      const contentHeight = layout.clientHeight - pad * 2;
      const maxTranslate = Math.max(0, contentHeight - side.offsetHeight);
      let t = top - rect.top - pad;
      t = Math.max(0, Math.min(t, maxTranslate));
      side.style.transform = t > 0 ? `translateY(${t}px)` : '';
    }

    // Cards carry whichever grouping the merchant chose (product title or an
    // option value). Build one chip per distinct value, in the order the
    // variants are rendered; no values means no chip bar at all.
    buildChips() {
      if (!this.chipsRow) return;
      const values = [];
      this.cards.forEach((card) => {
        const cat = (card.dataset.category || '').trim();
        if (cat && !values.includes(cat)) values.push(cat);
      });
      if (values.length < 2) return;

      const chip = (value, label, pressed) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'sbb-chip';
        b.dataset.sbbChip = value;
        b.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        b.textContent = label;
        return b;
      };
      this.chipsRow.appendChild(chip('All', 'All', true));
      values.forEach((v) => this.chipsRow.appendChild(chip(v, v, false)));
      if (this.chipBar) this.chipBar.hidden = false;
    }

    // Show each scroll arrow only when there's room to scroll that way.
    updateChipArrows() {
      const el = this.chipsRow;
      if (!el) return;
      const overflow = el.scrollWidth > el.clientWidth + 4;
      const atStart = el.scrollLeft <= 4;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      if (this.chipPrev) this.chipPrev.hidden = !overflow || atStart;
      if (this.chipNext) this.chipNext.hidden = !overflow || atEnd;
    }

    // On mobile the summary panel is a fixed bottom card; reserve space below
    // the content equal to its collapsed height so nothing hides behind it.
    updateStickyOffset() {
      if (!this.panel) return;
      const mobile = window.matchMedia('(max-width: 959px)').matches;
      if (!mobile) { this.root.style.setProperty('--sbb-panel-pad', '0px'); return; }
      if (!this.panel.classList.contains('is-open')) {
        this.collapsedHeight = this.panel.offsetHeight;
      }
      this.root.style.setProperty('--sbb-panel-pad', (this.collapsedHeight || 0) + 'px');
    }

    bind() {
      if (this.chipsRow) {
        this.chipsRow.addEventListener('click', (e) => {
          const chip = e.target.closest('[data-sbb-chip]');
          if (!chip) return;
          this.category = chip.dataset.sbbChip;
          this.chipsRow.querySelectorAll('[data-sbb-chip]').forEach((c) => {
            c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
          });
          this.filter();
        });

        if (this.chipPrev) {
          this.chipPrev.addEventListener('click', () => this.chipsRow.scrollBy({ left: -160, behavior: 'smooth' }));
        }
        if (this.chipNext) {
          this.chipNext.addEventListener('click', () => this.chipsRow.scrollBy({ left: 160, behavior: 'smooth' }));
        }
        this.chipsRow.addEventListener('scroll', () => this.updateChipArrows(), { passive: true });
      }

      this.grid.addEventListener('click', (e) => {
        const card = e.target.closest('[data-sbb-card]');
        if (!card || card.hasAttribute('data-unavailable')) return;
        if (e.target.closest('[data-sbb-add]')) {
          // Without the stepper the Add button toggles the item instead.
          if (!this.quantityEnabled && this.countOf(card.dataset.variantId) > 0) {
            this.removeOne(card.dataset.variantId);
          } else {
            this.add(card.dataset.variantId);
          }
          return;
        }
        if (e.target.closest('[data-sbb-plus]')) { this.add(card.dataset.variantId); return; }
        if (e.target.closest('[data-sbb-minus]')) { this.removeOne(card.dataset.variantId); }
      });

      // The mandatory item can't be added or removed, but its variant can be
      // swapped when the product has more than one.
      if (this.requiredCard) {
        this.requiredCard.addEventListener('change', (e) => {
          const select = e.target.closest('[data-sbb-variant]');
          if (select) this.changeVariant(select);
        });
      }

      this.tierEls.forEach((tier) => {
        tier.addEventListener('click', () => {
          // Tapping a tier nudges focus to the grid so customers can fill it.
          this.grid.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      });

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => { this.picks = []; this.render(); });
      }

      this.purchaseOpts.forEach((opt) => {
        opt.addEventListener('click', () => {
          this.purchaseType = opt.dataset.sbbPurchaseOpt;
          this.purchaseOpts.forEach((o) => o.setAttribute('aria-pressed', o === opt ? 'true' : 'false'));
          this.renderFooter();
        });
      });

      this.cta.addEventListener('click', () => this.addToCart());

      if (this.peekToggle) {
        this.peekToggle.addEventListener('click', () => {
          const open = this.panel.classList.toggle('is-open');
          this.peekToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          this.updateStickyOffset();
        });
      }

      this.accordionTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const panel = trigger.nextElementSibling;
          const expanded = trigger.getAttribute('aria-expanded') === 'true';
          trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
          if (panel) panel.hidden = expanded;
        });
      });
    }

    findCard(variantId) {
      return this.cards.find((c) => c.dataset.variantId === variantId);
    }

    // The mandatory item only counts towards the price when it's in stock.
    requiredAvailable() {
      return !!this.requiredCard && !this.requiredCard.hasAttribute('data-unavailable');
    }

    requiredPrice() {
      if (!this.requiredAvailable()) return 0;
      return parseInt(this.requiredCard.dataset.price, 10) || 0;
    }

    // Switch the mandatory item to a different variant: update its
    // id/price/image and re-render.
    changeVariant(select) {
      const card = select.closest('[data-sbb-required-card]');
      if (!card) return;
      const opt = select.options[select.selectedIndex];
      const newId = select.value;
      const newPrice = (opt && opt.dataset.price) || card.dataset.price;

      card.dataset.variantId = newId;
      card.dataset.price = newPrice;
      if (opt && opt.dataset.image) card.dataset.image = opt.dataset.image;
      // A variant swap can move the item in or out of stock.
      if (opt && opt.disabled) {
        card.setAttribute('data-unavailable', '');
      } else {
        card.removeAttribute('data-unavailable');
      }

      // Swap the product image to match the selected variant.
      if (opt && opt.dataset.cardImage) {
        const mediaImg = card.querySelector('img');
        if (mediaImg) {
          mediaImg.srcset = '';
          mediaImg.src = opt.dataset.cardImage;
        }
      }

      const priceEl = card.querySelector('[data-sbb-card-price]');
      if (priceEl) priceEl.textContent = this.money(parseInt(newPrice, 10) || 0);

      this.render();
    }

    countOf(variantId) {
      return this.picks.filter((id) => id === variantId).length;
    }

    add(variantId) {
      if (!this.quantityEnabled && this.countOf(variantId) > 0) return;
      if (this.picks.length >= this.maxChoices) {
        this.showToast(`That's the most you can add — ${this.maxChoices} per bundle.`);
        return;
      }
      this.picks.push(variantId);
      this.render();
    }

    removeOne(variantId) {
      const i = this.picks.lastIndexOf(variantId);
      if (i < 0) return;
      this.picks.splice(i, 1);
      this.render();
    }

    removeAt(index) {
      this.picks.splice(index, 1);
      this.render();
    }

    filter() {
      let visible = 0;
      this.cards.forEach((card) => {
        const cat = card.dataset.category || '';
        const show = this.category === 'All' || cat === this.category;
        card.hidden = !show;
        if (show) visible += 1;
      });
      this.empty.classList.toggle('is-visible', visible === 0);
      const noun = this.itemNoun;
      const label = visible === 1 ? noun.replace(/s$/, '') : noun;
      const suffix = this.category !== 'All' ? ` · ${this.category}` : '';
      if (this.countline) this.countline.textContent = `${visible} ${label}${suffix}`;
    }

    // Largest tier whose size <= chosen count; null if below the smallest.
    tierForCount(count) {
      let match = null;
      this.tiers.forEach((t) => { if (count >= t.size) match = t; });
      return match;
    }

    // Full (undiscounted) price of everything currently in the bundle.
    fullPrice() {
      const chosen = this.picks.reduce((sum, id) => {
        const card = this.findCard(id);
        return sum + (card ? parseInt(card.dataset.price, 10) || 0 : 0);
      }, 0);
      return chosen + this.requiredPrice();
    }

    // Bundle price in pence. `locked` means the minimum isn't met yet, so no
    // bundle pricing applies and we just show the running total.
    priceBreakdown() {
      const count = this.picks.length;
      const full = this.fullPrice();
      if (count < this.minChoices) return { price: full, full, locked: true };

      if (this.pricingMode === 'percent') {
        const price = Math.round(full * (1 - this.percentDiscount / 100));
        return { price, full, locked: false };
      }

      const tier = this.tierForCount(count);
      if (!tier) return { price: full, full, locked: true };

      // Anything chosen beyond the largest tier is charged at its own price.
      // The tier covers the dearest items so the extras cost the customer
      // as little as possible.
      const pickPrices = this.picks
        .map((id) => {
          const card = this.findCard(id);
          return card ? parseInt(card.dataset.price, 10) || 0 : 0;
        })
        .sort((a, b) => b - a);
      const extras = pickPrices.slice(tier.size).reduce((sum, p) => sum + p, 0);

      const price = tier.price + extras + (this.mandatoryIncluded ? 0 : this.requiredPrice());
      return { price, full, locked: false };
    }

    applySubscription(cents) {
      if (this.purchaseType !== 'subscribe' || !this.subscribeDiscount) return cents;
      return Math.round(cents * (1 - this.subscribeDiscount / 100));
    }

    renderCards() {
      const atMax = this.picks.length >= this.maxChoices;
      this.cards.forEach((card) => {
        const count = this.countOf(card.dataset.variantId);
        const selected = count > 0;
        card.classList.toggle('is-selected', selected);
        const countEl = card.querySelector('[data-sbb-count]');
        if (countEl) countEl.textContent = count;
        const unavailable = card.hasAttribute('data-unavailable');
        const plus = card.querySelector('[data-sbb-plus]');
        if (plus) plus.disabled = atMax || unavailable;
        const addBtn = card.querySelector('[data-sbb-add]');
        if (!addBtn) return;
        if (this.quantityEnabled) {
          addBtn.disabled = atMax || unavailable;
        } else {
          // A selected card stays clickable at the maximum so it can be
          // toggled back off.
          addBtn.disabled = unavailable || (atMax && !selected);
          addBtn.setAttribute('aria-pressed', selected ? 'true' : 'false');
        }
      });
    }

    renderTiers() {
      const count = this.picks.length;
      const activeTier = this.tierForCount(count);
      this.tierEls.forEach((el) => {
        const size = parseInt(el.dataset.size, 10);
        const unlocked = count >= size;
        el.classList.toggle('is-unlocked', unlocked);
        el.classList.toggle('is-active', activeTier ? size === activeTier.size : false);
        const hint = el.querySelector('[data-sbb-tier-hint]');
        if (hint) {
          if (!unlocked && size > count) {
            hint.textContent = `+${size - count} to unlock`;
          } else {
            hint.textContent = '';
          }
        }
      });
    }

    renderProgress() {
      const count = this.picks.length;
      const ready = count >= this.minChoices;

      // Small "Add to cart" CTA on the collapsed sticky cart, once the bundle
      // is complete. Tapping it expands the panel (handled by the peek).
      if (this.peekCta) this.peekCta.hidden = !ready;
      if (!this.progressLabel) return;

      const firstTier = this.tiers.find((t) => t.size >= this.minChoices);
      if (!ready) {
        const need = this.minChoices - count;
        const goal = firstTier ? ` → ${this.money(firstTier.price)} bundle` : '';
        this.progressLabel.innerHTML = count === 0
          ? `<strong class="sbb-progress__hl">Choose ${this.minChoices}</strong> to complete your bundle${goal}`
          : `<strong class="sbb-progress__hl">Add ${need} more</strong>${goal} 🔥`;
        return;
      }

      const next = this.pricingMode === 'tiers' ? this.tiers.find((t) => t.size > count) : null;
      if (next && count < this.maxChoices) {
        const need = next.size - count;
        this.progressLabel.innerHTML = `<strong class="sbb-progress__hl">Add ${need} more</strong> → ${this.money(next.price)} — even better value`;
      } else {
        const total = this.requiredCard ? count + 1 : count;
        this.progressLabel.innerHTML = `<strong>Bundle ready!</strong> ${total} items 🎉`;
      }
    }

    // The mandatory item, the chosen items, then dashed slots for whatever is
    // still needed to reach the minimum.
    renderPicks() {
      this.picksEl.innerHTML = '';

      if (this.requiredCard) {
        const locked = document.createElement('span');
        locked.className = 'sbb-pick sbb-pick--locked';
        locked.title = `${this.requiredCard.dataset.name} is included in every bundle`;
        const img = document.createElement('span');
        img.className = 'sbb-pick__img';
        if (this.requiredCard.dataset.image) img.style.backgroundImage = `url('${this.requiredCard.dataset.image}')`;
        locked.appendChild(img);
        const lock = document.createElement('span');
        lock.className = 'sbb-pick__lock';
        lock.innerHTML = SVG_LOCK;
        locked.appendChild(lock);
        this.picksEl.appendChild(locked);
      }

      this.picks.forEach((variantId, index) => {
        const card = this.findCard(variantId);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sbb-pick';
        button.setAttribute('aria-label', `Remove ${card ? card.dataset.name : 'item'}`);
        const img = document.createElement('span');
        img.className = 'sbb-pick__img';
        if (card && card.dataset.image) img.style.backgroundImage = `url('${card.dataset.image}')`;
        button.appendChild(img);
        const remove = document.createElement('span');
        remove.className = 'sbb-pick__remove';
        remove.innerHTML = SVG_X;
        button.appendChild(remove);
        button.addEventListener('click', () => this.removeAt(index));
        this.picksEl.appendChild(button);
      });

      const remaining = Math.max(0, this.minChoices - this.picks.length);
      for (let i = 0; i < remaining; i += 1) {
        const slot = document.createElement('span');
        slot.className = 'sbb-pick-placeholder';
        slot.textContent = this.picks.length + i + 1;
        this.picksEl.appendChild(slot);
      }
    }

    renderFooter() {
      const count = this.picks.length;
      const { price, full, locked } = this.priceBreakdown();
      const displayPrice = locked ? price : this.applySubscription(price);
      const displaySavings = locked ? 0 : Math.max(0, full - displayPrice);

      this.renderPicks();

      // Price
      const nothingYet = count === 0 && !this.requiredCard;
      this.priceEl.textContent = this.money(nothingYet ? 0 : displayPrice);
      this.priceEl.classList.toggle('has-savings', displaySavings > 0);
      if (displaySavings > 0) {
        this.fullPriceEl.textContent = this.money(full);
        this.fullPriceEl.style.display = '';
        this.saveEl.textContent = `Save ${this.money(displaySavings)}`;
        this.saveEl.style.display = '';
      } else {
        this.fullPriceEl.style.display = 'none';
        this.saveEl.style.display = 'none';
      }

      // CTA
      const soldOut = !!this.requiredCard && !this.requiredAvailable();
      const ready = count >= this.minChoices && !soldOut;
      this.cta.disabled = !ready;
      if (soldOut) {
        this.cta.textContent = 'Currently unavailable';
      } else if (!ready) {
        const need = Math.max(0, this.minChoices - count);
        this.cta.textContent = count === 0
          ? (this.cta.dataset.emptyLabel || 'Add to build your bundle')
          : `Add ${need} more to build your bundle`;
      } else {
        const verb = this.purchaseType === 'subscribe' ? 'Subscribe' : 'Add bundle';
        this.cta.textContent = `${verb} · ${this.money(displayPrice)}`;
      }

      if (this.clearBtn) this.clearBtn.hidden = count === 0;
    }

    render() {
      this.renderCards();
      this.renderTiers();
      this.renderProgress();
      this.renderFooter();
      this.updateStickyOffset();
      this.updateSticky();
    }

    showToast(message) {
      this.toastInner.textContent = message;
      this.toast.classList.add('is-visible');
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => this.toast.classList.remove('is-visible'), 2400);
    }

    addToCart() {
      if (this.picks.length < this.minChoices || this.cta.disabled) return;
      const subscribe = this.purchaseType === 'subscribe';

      const grouped = {};
      const addLine = (card) => {
        if (!card) return;
        const id = card.dataset.variantId;
        const sellingPlan = subscribe ? card.dataset.sellingPlan : '';
        const key = `${id}::${sellingPlan || ''}`;
        if (!grouped[key]) grouped[key] = { id: parseInt(id, 10), quantity: 0, sellingPlan };
        grouped[key].quantity += 1;
      };

      // The mandatory item always goes in first.
      if (this.requiredAvailable()) addLine(this.requiredCard);
      this.picks.forEach((id) => addLine(this.findCard(id)));

      const items = Object.values(grouped).map((g) => {
        const item = { id: g.id, quantity: g.quantity };
        if (g.sellingPlan) item.selling_plan = parseInt(g.sellingPlan, 10);
        return item;
      });

      this.cta.disabled = true;
      const originalLabel = this.cta.textContent;
      this.cta.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      })
        .then((response) => {
          if (!response.ok) return response.json().then((err) => Promise.reject(err));
          return response.json();
        })
        .then((data) => {
          this.picks = [];
          this.render();
          this.showToast('Bundle added to your bag! 💕');
          document.dispatchEvent(new CustomEvent('sbb:added-to-cart', { detail: data, bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch((err) => {
          const message = (err && err.description) || 'Something went wrong — please try again.';
          this.showToast(message);
          this.cta.disabled = false;
          this.cta.textContent = originalLabel;
        });
    }
  }

  function init() {
    document.querySelectorAll('[data-skincare-bundle-builder-v2]').forEach((root) => {
      if (root.__sbbV2Initialised) return;
      root.__sbbV2Initialised = true;
      new SkincareBundleBuilderV2(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();